import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { getPhonePosition } from '../../config/sceneConfig';
import sceneConfig from '../../config/sceneConfig';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import '../../styles/SoundWaves.css';

const SoundWaves = React.memo(() => {
  const { phoneboxState, getSceneObject } = useCanvasContext();
  const { camera } = useThree();
  const [audioIntensity, setAudioIntensity] = useState(0);
  const [waves, setWaves] = useState([]);
  const [lastWaveTime, setLastWaveTime] = useState(0);
  const [soundStartTime, setSoundStartTime] = useState(null);
  const [soundDuration, setSoundDuration] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [fadeOutStartTime, setFadeOutStartTime] = useState(null);
  const [worldPosition, setWorldPosition] = useState([0, 0, 0]);
  const breakpoint = useBreakpoint();
  const animationRef = useRef();
  const lastUpdateTimeRef = useRef(0); // Throttle animation updates
  const groupRef = useRef();
  const lastPositionUpdateRef = useRef(0);
  const positionUpdateThrottle = 16; // ~60fps max updates

  // Get offsets from sceneConfig with responsive overrides
  const offsets = useMemo(() => {
    const soundWavesConfig = sceneConfig.soundWaves;
    if (!soundWavesConfig) {
      return {
        depthOffset: -1,
        horizontalOffset: -0.6,
        verticalOffset: 1.35,
        scale: 0.4
      };
    }

    // Get default values
    const defaultDepthOffset = soundWavesConfig.depthOffset ?? -1;
    const defaultHorizontalOffset = soundWavesConfig.horizontalOffset ?? -0.6;
    const defaultVerticalOffset = soundWavesConfig.verticalOffset ?? 1.35;
    const defaultScale = soundWavesConfig.scale ?? 0.4;

    // Determine responsive breakpoint using the hook value
    const breakpoints = soundWavesConfig.responsive?.breakpoints;
    let responsiveConfig = null;
    
    if (breakpoints) {
      responsiveConfig = breakpoints[breakpoint];
    }
    
    // Use responsive overrides if specified, otherwise use defaults
    return {
      depthOffset: responsiveConfig?.depthOffset ?? defaultDepthOffset,
      horizontalOffset: responsiveConfig?.horizontalOffset ?? defaultHorizontalOffset,
      verticalOffset: responsiveConfig?.verticalOffset ?? defaultVerticalOffset,
      scale: responsiveConfig?.scale ?? defaultScale
    };
  }, [breakpoint]);

  // Update position in 3D world space relative to phone (similar to CameraAlignedLabels)
  useFrame(() => {
    const scene = getSceneObject('phonebox-scene');
    if (!scene) {
      // Debug: log when scene is not available
      if (Math.random() < 0.01) {
        console.log('SoundWaves: phonebox-scene not found');
      }
      return;
    }

    // Get phone position
    const phonePosition = getPhonePosition(scene);
    if (!phonePosition) {
      // Debug: log when phone position is not available
      if (Math.random() < 0.01) {
        console.log('SoundWaves: phone position not available');
      }
      return;
    }

    // Calculate base world position relative to phone
    const base = new THREE.Vector3();
    if (Array.isArray(phonePosition)) {
      base.fromArray(phonePosition);
    } else {
      base.copy(phonePosition);
    }

    // Calculate position relative to camera (similar to CameraAlignedLabels)
    const cameraWorld = new THREE.Vector3();
    camera.getWorldPosition(cameraWorld);
    
    // Move toward camera by depthOffset
    const camDir = cameraWorld.clone().sub(base).normalize();
    const toCamera = camDir.multiplyScalar(offsets.depthOffset);

    // Calculate horizontal direction relative to camera view (not scene rotation)
    // Use camera's local coordinate system to ensure consistent left/right positioning
    const cameraRight = new THREE.Vector3(1, 0, 0);
    cameraRight.applyQuaternion(camera.quaternion);
    cameraRight.normalize();
    
    // Use camera's local up vector for consistent vertical positioning
    const cameraUp = new THREE.Vector3(0, 1, 0);
    cameraUp.applyQuaternion(camera.quaternion);
    cameraUp.normalize();
    
    // Use camera's coordinate system (ensures left is always on the left from camera's perspective)
    const right = cameraRight;
    const up = cameraUp;

    // Calculate final position using offsets from config
    const finalPosition = base
      .clone()
      .add(right.clone().multiplyScalar(offsets.horizontalOffset))
      .add(up.clone().multiplyScalar(offsets.verticalOffset))
      .add(toCamera);

    // Throttle position updates
    const now = performance.now();
    if (now - lastPositionUpdateRef.current >= positionUpdateThrottle) {
      const newPos = finalPosition.toArray();
      setWorldPosition(newPos);
      lastPositionUpdateRef.current = now;
    }

    // Billboard the group to face the camera
    if (groupRef.current) {
      groupRef.current.quaternion.copy(camera.quaternion);
    }
  });

  // Update audio intensity and track sound duration
  useEffect(() => {
    if (phoneboxState.audioData && phoneboxState.isPlaying) {
      // Reset fade-out state when audio starts playing
      if (isFadingOut) {
        setIsFadingOut(false);
        setFadeOutStartTime(null);
      }
      
      // Use the average amplitude to drive visual intensity
      const intensity = phoneboxState.audioData.average / 255; // Normalize to 0-1
      
      // Track sound start time
      if (!soundStartTime) {
        setSoundStartTime(Date.now());
      }
      
      // Calculate sound duration
      const currentDuration = Date.now() - (soundStartTime || Date.now());
      setSoundDuration(currentDuration);
      
      // Apply less smoothing for more sensitivity
      setAudioIntensity(prev => {
        const smoothingFactor = 0.7; // Increased from 0.3 for faster response
        return prev * (1 - smoothingFactor) + intensity * smoothingFactor;
      });
    } else {
      // When audio stops, start fade-out if we have waves and aren't already fading
      if (waves.length > 0 && !isFadingOut) {
        setIsFadingOut(true);
        setFadeOutStartTime(Date.now());
      }
      
      setAudioIntensity(0);
      setSoundStartTime(null);
      setSoundDuration(0);
    }
  }, [phoneboxState.audioData, phoneboxState.isPlaying, soundStartTime, isFadingOut, waves.length]);

  // Wave generation and animation loop
  useEffect(() => {
    // Show soundwaves when:
    // 1. Audio is playing (intro, questions, or hangup), OR
    // 2. Currently fading out
    // For intro audio: animation state is pickup AND in hold position AND playing
    // For questions: in question mode AND playing
    // For hangup: animation state is hangup AND playing
    const shouldAnimate = phoneboxState.isPlaying && 
      (phoneboxState.isInQuestionMode || 
       phoneboxState.animationState === 'hangup' || 
       (phoneboxState.animationState === 'pickup' && phoneboxState.isInHoldPosition));
    
    if (!shouldAnimate && !isFadingOut) {
      setWaves([]);
      return;
    }
    
    // If fading out and fade duration is complete, clear waves
    if (isFadingOut && fadeOutStartTime) {
      const fadeOutDuration = 300; // 300ms fade-out duration
      if (Date.now() - fadeOutStartTime > fadeOutDuration) {
        setWaves([]);
        setIsFadingOut(false);
        setFadeOutStartTime(null);
        return;
      }
    }

    const generateNewWave = () => {
      const centerY = 60; // Moved higher up from 75 to 60
      
      // Create tighter cone shape - waves start closer together
      const coneAngle = (Math.random() - 0.5) * 0.6; // Reduced cone angle for tighter grouping
      const startY = centerY + coneAngle * 30; // Reduced spread at start
      
      // Calculate wave direction based on vertical position
      // Top waves go up at 45°, bottom waves go down at 45°
      const normalizedY = (startY - centerY) / 20; // Normalize to -1 to 1 range (reduced range)
      const waveDirection = normalizedY * 45; // 45 degree angle based on position
      
      // Calculate length based on sound duration - longer sounds = longer waves
      const durationMultiplier = Math.min(3, 1 + (soundDuration / 2000)); // Max 3x length for sounds over 2 seconds
      const baseLength = (20 + Math.random() * 30) * durationMultiplier; // Duration-based length
      
      return {
        id: Math.random(),
        startY: startY,
        coneAngle: coneAngle, // Store cone angle for spreading effect
        waveDirection: waveDirection, // Store the 45-degree direction
        baseLength: baseLength, // Store base length for dynamic calculation
        amplitude: 4 + Math.random() * 8 + (audioIntensity * 10),
        frequency: 0.2 + Math.random() * 0.3,
        speed: 3 + Math.random() * 4, // Even faster speed
        phase: Math.random() * Math.PI * 2,
        startTime: Date.now(),
        lifeDuration: 500 + Math.random() * 800, // Much quicker lifespan (0.5-1.3 seconds)
        segments: 12 + Math.floor(Math.random() * 8), // Fewer segments for quicker rendering
        strokeWidth: 1.5 + Math.random() * 1.5,
        opacity: 0.8 + Math.random() * 0.2
      };
    };

    const animate = () => {
      const now = Date.now();
      
      // Throttle animation to 30fps to prevent performance issues
      if (now - lastUpdateTimeRef.current < 33) { // ~30fps
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateTimeRef.current = now;
      
      setWaves(prevWaves => {
        // Remove expired waves
        const activeWaves = prevWaves.filter(wave => 
          now - wave.startTime < wave.lifeDuration
        );
        
        // Much more sensitive threshold for wave generation
        const audioThreshold = 0.03; // Much lower threshold for more sensitivity
        const isAudioActive = audioIntensity > audioThreshold && !isFadingOut;
        
        if (isAudioActive) {
          // Generate waves based on volume - quiet = fewer lines, loud = more lines
          const volumeBasedRate = 0.2 + (audioIntensity * 0.8); // Volume affects generation rate
          const shouldAddWave = Math.random() < volumeBasedRate;
          
          // Calculate max waves based on volume - quiet = fewer, loud = more
          const maxWaves = Math.floor(2 + (audioIntensity * 6)); // 2-8 waves based on volume
          
          // Very short minimum gap between waves for audio-like rapid bursts
          const timeSinceLastWave = now - lastWaveTime;
          const minGapBetweenWaves = 20; // Very short gap (20ms) for rapid audio bursts
          
          if (shouldAddWave && activeWaves.length < maxWaves && timeSinceLastWave > minGapBetweenWaves) {
            activeWaves.push(generateNewWave());
            setLastWaveTime(now);
          }
        } else {
          // When audio is quiet, reset the last wave time to allow immediate generation when audio returns
          setLastWaveTime(0);
        }
        // When audio is quiet, no new waves are generated (natural gaps)
        
        return activeWaves;
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [phoneboxState.isPlaying, phoneboxState.isInHoldPosition, phoneboxState.animationState, phoneboxState.isInQuestionMode, audioIntensity, isFadingOut, fadeOutStartTime, lastWaveTime, soundDuration]);

  // Show sound waves when:
  // 1. Audio is playing (intro, questions, or hangup), OR
  // 2. Currently fading out
  // For intro audio: animation state is pickup AND in hold position AND playing
  // For questions: in question mode AND playing
  // For hangup: animation state is hangup AND playing
  const shouldShow = 
    isFadingOut ||
    (phoneboxState.isPlaying && (
      phoneboxState.isInQuestionMode || // Questions
      phoneboxState.animationState === 'hangup' || // Hangup audio
      (phoneboxState.animationState === 'pickup' && phoneboxState.isInHoldPosition) // Intro audio
    ));
  
  // Always render the group for positioning, but only show content when shouldShow is true
  // This ensures the Html component is positioned correctly even when not visible

  // Generate individual wave path with realistic cone shape, 45-degree angles, and fade effects
  const generateWavePath = (wave) => {
    const elapsed = Date.now() - wave.startTime;
    const progress = elapsed / wave.lifeDuration;
    
    // Calculate dynamic length based on current audio intensity
    const audioLengthMultiplier = 1 + (audioIntensity * 3); // Increased multiplier for more dramatic effect
    const currentLength = wave.baseLength * audioLengthMultiplier;
    
    const currentX = progress * (currentLength + 40); // Use dynamic length
    
    // Calculate fade-out opacity based on progress - faster fade for audio-like effect
    const fadeStart = 0.6; // Start fading earlier at 60% of lifespan
    const fadeProgress = Math.max(0, (progress - fadeStart) / (1 - fadeStart));
    const fadeOpacity = 1 - fadeProgress;
    
    // Calculate fade-in opacity for smooth emergence - faster fade-in
    const fadeInDuration = 0.1; // Faster fade in over first 10% of lifespan
    const fadeInProgress = Math.min(1, progress / fadeInDuration);
    const fadeInOpacity = fadeInProgress;
    
    let path = `M 0,${wave.startY}`;
    let firstPoint = true;
    
    for (let i = 0; i <= wave.segments; i++) {
      const x = (i / wave.segments) * currentLength; // Use dynamic current length
      const globalX = currentX - currentLength + x + 30; // Start further from left side (30px offset)
      
      if (globalX >= 0 && globalX <= 200) { // Reduced visible area
        // Apply 45-degree angle based on wave direction
        const angleRadians = (wave.waveDirection * Math.PI) / 180; // Convert degrees to radians
        const angledY = wave.startY + Math.tan(angleRadians) * x; // Calculate Y based on 45-degree angle
        
        // Create cone shape - waves spread out as they travel based on their direction
        const coneSpread = wave.coneAngle * (x / currentLength) * 0.4; // Reduced spread factor for tighter cone
        const currentY = angledY + coneSpread;
        
        // Base wave phase
        const basePhase = (x / 15) * Math.PI * 2 * wave.frequency + wave.phase; // Adjusted for shorter waves
        
        // Add subtle squiggle in the direction of travel
        const squiggleTime = elapsed * 0.002; // Much slower, subtle squiggle
        const squigglePhase = squiggleTime + (x / 20) * Math.PI * 2 * 0.2; // Very gentle squiggle
        const squiggleAmplitude = 1 + Math.sin(squiggleTime * 0.3) * 0.5; // Much smaller amplitude
        const squiggle = Math.sin(squigglePhase) * squiggleAmplitude;
        
        // Add breathing effect - waves get slightly larger/smaller over time
        const breathingEffect = 1 + Math.sin(elapsed * 0.002) * 0.05; // Very subtle breathing
        
        // Combine effects with cone shape and 45-degree angle - much cleaner
        const totalPhase = basePhase + squiggle * 0.05; // Very minimal squiggle influence
        const y = currentY + Math.sin(totalPhase) * wave.amplitude * breathingEffect + squiggle;
        
        // Apply fade-in effect to the beginning of the wave
        const segmentProgress = i / wave.segments;
        const segmentFadeIn = Math.min(1, segmentProgress / 0.25); // Faster fade-in over first 25% of wave length
        const segmentOpacity = segmentFadeIn * fadeInOpacity;
        
        // Only add points that are visible (above fade-in threshold)
        if (segmentOpacity > 0.1) {
          if (firstPoint) {
            path = `M ${globalX},${y}`;
            firstPoint = false;
          } else {
            path += ` L ${globalX},${y}`;
          }
        }
      }
    }
    
    return { path, opacity: fadeOpacity * fadeInOpacity };
  };

  // Use transform mode so distanceFactor affects perspective scaling
  // Apply scale to distanceFactor to affect the entire container
  const baseDistanceFactor = 10;
  const uiScale = offsets.scale;
  const df = baseDistanceFactor * uiScale;

  // Debug: log when component renders
  useEffect(() => {
    console.log('SoundWaves component mounted');
    return () => {
      console.log('SoundWaves component unmounted');
    };
  }, []);

  // Always render the group, even if position hasn't been calculated yet
  // Use a default position if worldPosition is still [0,0,0] after a delay
  const displayPosition = useMemo(() => {
    return worldPosition[0] === 0 && worldPosition[1] === 0 && worldPosition[2] === 0 
      ? [0, 0, 5] // Default position slightly in front of camera
      : worldPosition;
  }, [worldPosition]);

  // Log render (throttled to avoid spam)
  useEffect(() => {
    const logInterval = setInterval(() => {
      console.log('SoundWaves render check - displayPosition:', displayPosition, 'worldPosition:', worldPosition, 'shouldShow:', shouldShow);
    }, 2000);
    return () => clearInterval(logInterval);
  }, [displayPosition, worldPosition, shouldShow]);

  return (
    <group ref={groupRef} position={displayPosition}>
      <Html 
        transform 
        distanceFactor={df}
        occlude={false}
        center
        style={{ pointerEvents: 'none' }}
      >
        <div 
          style={{
            transformOrigin: 'center center',
            pointerEvents: 'none',
            width: '250px',
            height: '150px',
            position: 'relative',
          }}
        >
          {/* Simple cone-shaped voice lines */}
          <svg
            width="250"
            height="150"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            {/* Define gradient for smooth fade-in effect */}
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#888888" stopOpacity="0" />
                <stop offset="30%" stopColor="#888888" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#888888" stopOpacity="1" />
              </linearGradient>
            </defs>
            
            {/* Individual traveling waves with wiggling and fade-out */}
            {waves.map((wave) => {
              const waveData = generateWavePath(wave);
              
              // Calculate fade-out opacity for the entire component
              let fadeOutOpacity = 1;
              if (isFadingOut && fadeOutStartTime) {
                const fadeOutDuration = 300; // 300ms fade-out duration
                const fadeOutProgress = Math.min(1, (Date.now() - fadeOutStartTime) / fadeOutDuration);
                fadeOutOpacity = 1 - fadeOutProgress;
              }
              
              return (
                <path
                  key={wave.id}
                  d={waveData.path}
                  stroke="url(#waveGradient)"
                  strokeWidth={wave.strokeWidth}
                  fill="none"
                  strokeLinecap="round"
                  opacity={wave.opacity * waveData.opacity * fadeOutOpacity} // Combine base opacity with fade-out and component fade-out
                  style={{
                    transformOrigin: '0 0',
                  }}
                />
              );
            })}
          </svg>
        </div>
      </Html>
    </group>
  );
});

export default SoundWaves;
